import { AppBar, Checkbox, Dialog, DialogActions, DialogContent, Input, Table, TableBody, TableCell, TableRow, Toolbar, Typography } from '@mui/material';
import { withStyles, WithStyles } from "@mui/styles";
import SearchIcon from '@mui/icons-material/Search';
import genericListsStyles from 'assets/jss/genericListsStyles';
import BoxDrawer from 'components/BoxDrawer';
import IconButtonClose from 'components/Buttons/IconButtonClose';
import { SpinnerIndicator } from 'components/CallServer';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiPagination from 'components/MuiExtras/MuiPagination';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import MuiPushRight from 'components/MuiExtras/MuiPushRight';
import { GenericListItemDto, PagedResult } from 'models/ApiModels';
import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export interface IGenericListSelectionProps {
    onSelect: (values: GenericListItemDto[], multiSelection: boolean) => void;
    multi: boolean;
};

interface IProps extends IGenericListSelectionProps {
    title: string;
    showThumbnail: boolean;
    variant?: "dialog" | "drawer";
    onGetData: (searchText: string, offset: number, take: number) => Promise<PagedResult<GenericListItemDto>>;
}

interface IState {
    ds: PagedResult<GenericListItemDto>;
    page: number;
    loading: boolean,
    loadingError: boolean,
    selected: Array<GenericListItemDto>;
}

type Props = IProps & WithTranslation & WithStyles<typeof genericListsStyles>;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const rowsPerPage = 7;

class GenericListSelection extends React.Component<Props, IState> {

    private searchRef: any = null;

    static defaultProps: Partial<IProps> = {
        variant: "dialog"
    };

    constructor(props: Props) {
        super(props);


        this.state = {
            ds: { items: [], total: 0 },
            selected: [],
            page: 0,
            loading: true,
            loadingError: false,
        };
    }

    private handleClick(event: any, item: GenericListItemDto) {
        const { selected } = this.state;
        const id = item.id;
        let newSelected: Array<GenericListItemDto> = [];

        if (this.props.multi) {
            const selectedIndex = selected.findIndex(x => x.id == id);
            if (selectedIndex === -1) {
                newSelected = newSelected.concat(selected, item);
            } else if (selectedIndex === 0) {
                newSelected = newSelected.concat(selected.slice(1));
            } else if (selectedIndex === selected.length - 1) {
                newSelected = newSelected.concat(selected.slice(0, -1));
            } else if (selectedIndex > 0) {
                newSelected = newSelected.concat(
                    selected.slice(0, selectedIndex),
                    selected.slice(selectedIndex + 1),
                );
            }
        }
        else {
            newSelected.push(item);
        }

        if (this.props.onSelect)
            this.props.onSelect(newSelected, this.props.multi);

        this.setState({ selected: newSelected });
    };

    private isSelected(id: any) {
        return this.state.selected.findIndex(x => x.id == id) !== -1;
    }

    private getData(page: number) {
        const searchText: string = this.searchRef ? this.searchRef.value : null;

        this.setState({ loading: true, loadingError: false, page: page });

        this.props.onGetData(searchText, page * rowsPerPage, rowsPerPage)
            .then((data) => {
                this.setState({ ds: data, loading: false, loadingError: false });
            })
            .catch(() => {
                this.setState({ loading: false, loadingError: true });
            });
    }

    private handleSearch(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
        if (e.keyCode == 13) this.getData(0);
    }

    private renderContent() {
        const { classes, showThumbnail } = this.props;
        const { ds, loading } = this.state;

        return (
            <GridEx container fullHeight direction="column" wrap="nowrap" spacing={2}>
                <GridEx>
                    <div className={classes.search}>
                        <div className={classes.searchIcon}>
                            <SearchIcon />
                        </div>
                        <Input
                            placeholder={this.props.t("Common.Input.Placeholder.SearchFor")}
                            disableUnderline
                            autoFocus
                            onKeyDown={(e) => this.handleSearch(e)}
                            inputRef={(ref) => this.searchRef = ref}
                            classes={{
                                root: classes.inputRoot,
                                input: classes.inputInput,
                            }}
                        />
                    </div>
                </GridEx>

                <GridEx item xs>
                    {
                        loading
                            ? < SpinnerIndicator />
                            : (
                                <Table className={classes.table}>
                                    <TableBody>
                                        {
                                            ds.items.map(n => {
                                                const isSelected = this.isSelected(n.id);
                                                return (
                                                    <TableRow
                                                        hover
                                                        onClick={event => this.handleClick(event, n)}
                                                        role="checkbox"
                                                        aria-checked={isSelected}
                                                        tabIndex={-1}
                                                        key={n.id}
                                                        selected={isSelected}

                                                    >
                                                        <TableCell padding="none" style={{ width: "1px" }} >
                                                            <Checkbox checked={isSelected} />
                                                        </TableCell>

                                                        {
                                                            showThumbnail &&
                                                            <TableCell padding="none" style={{ width: "1px" }}>
                                                                <img src={n.thumbnailUrl} alt={n.name} />
                                                            </TableCell>
                                                        }

                                                        <TableCell padding="none" >
                                                            {n.name}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        }
                                    </TableBody>
                                </Table>
                            )
                    }
                </GridEx>

            </GridEx>
        );
    }

    private renderDialog() {
        const { title, multi } = this.props;
        const { ds, page } = this.state;
        return (
            <Dialog
                open
                fullWidth
                maxWidth={"md"}
                onBackdropClick={() => { this.props.onSelect(null, multi); }}
                onClose={() => { this.props.onSelect(null, multi); }}
            >
                <DialogContent style={{ margin: 0, padding: 0 }}>

                    <AppBar position={"static"}>
                        <Toolbar>
                            <Typography variant="h6" color="inherit" noWrap>
                                {title}
                            </Typography>
                            <MuiPushRight>
                                <IconButtonClose color="inherit" onClick={() => this.props.onSelect(null, multi)} />
                            </MuiPushRight>
                        </Toolbar>
                    </AppBar>

                    <MuiPaper className={this.props.classes.tableContainer}>
                        {this.renderContent()}
                    </MuiPaper>

                </DialogContent>

                <DialogActions>
                    <MuiPagination
                        limit={rowsPerPage}
                        offset={page * rowsPerPage}
                        total={ds.total}
                        onClick={(e, offset, page) => this.getData(page - 1)}
                    />
                </DialogActions>
            </Dialog >
        );
    }

    private renderDrawer() {
        const { title, multi } = this.props;
        return (
            <BoxDrawer
                open
                title={title}
                overflow="auto"
                onClose={() => { this.props.onSelect(null, multi); }}
            >
                <MuiPaper fullHeight elevation={0}>
                    {this.renderContent()}
                </MuiPaper>
            </BoxDrawer>
        );
    }

    componentDidMount() {
        this.getData(0);
    }

    render() {
        return this.props.variant == "dialog"
            ? this.renderDialog()
            : this.renderDrawer();
    }
}

export default withTranslation()(withStyles(genericListsStyles)(GenericListSelection));